/**
 * SavingsStreak Component - Displays and celebrates savings streak progress
 * Features:
 * - Shows current streak and progress to next milestone
 * - Celebrates milestone achievements
 * - Displays streak titles and badges
 */

import { useState, useEffect } from 'react';
import { apiGet } from '@/utils/api';
import confetti from 'canvas-confetti';

// Streak milestone configurations
const STREAK_MILESTONES = [
  { days: 7, title: "Saver Starter", icon: "🌱", color: "from-green-400 to-emerald-500" },
  { days: 14, title: "Steady Stacker", icon: "📈", color: "from-blue-400 to-cyan-500" },
  { days: 30, title: "Money Master", icon: "💰", color: "from-yellow-400 to-amber-500" },
  { days: 60, title: "Savings Sensei", icon: "🥋", color: "from-orange-400 to-red-500" },
  { days: 90, title: "Wealth Builder", icon: "🏗️", color: "from-purple-400 to-violet-500" },
  { days: 180, title: "Financial Freedom", icon: "🦅", color: "from-pink-400 to-rose-500" },
  { days: 365, title: "Savings Legend", icon: "👑", color: "from-amber-400 to-yellow-500" }
];

export default function SavingsStreak({ className = '', onMilestoneReached = null }) {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebratedMilestone, setCelebratedMilestone] = useState(null);

  useEffect(() => {
    loadStreak();
  }, []);

  const loadStreak = async () => {
    try {
      const response = await apiGet('/goals/streak');
      if (response.ok) {
        const data = await response.json();
        setStreak(data);
        
        // Check for new milestone celebration
        const lastCelebrated = localStorage.getItem('last_savings_milestone');
        if (data.streak_title && data.streak_title.days > parseInt(lastCelebrated || '0')) {
          setCelebratedMilestone(data.streak_title);
          setShowCelebration(true);
          triggerCelebration();
          localStorage.setItem('last_savings_milestone', data.streak_title.days.toString());
          if (onMilestoneReached) {
            onMilestoneReached(data.streak_title);
          }
        }
      }
    } catch (error) {
      console.error('Error loading savings streak:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerCelebration = () => {
    // Fire confetti
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#10B981', '#F59E0B', '#8B5CF6']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#10B981', '#F59E0B', '#8B5CF6']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  const dismissCelebration = () => {
    setShowCelebration(false);
    setCelebratedMilestone(null);
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-24 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  if (!streak || streak.current_streak === 0) {
    // Show encouragement to start
    return (
      <div className={`bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200 ${className}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-2xl">💰</span>
          <div>
            <h3 className="font-bold text-emerald-900">Start Your Savings Streak!</h3>
            <p className="text-sm text-emerald-600">Add to any savings goal to begin</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-700">
          <span>🎯</span>
          <span>First milestone: <strong>7 days</strong> → Saver Starter 🌱</span>
        </div>
      </div>
    );
  }

  const currentStreak = streak.current_streak || 0;
  const nextMilestone = streak.next_milestone;
  const currentTitle = streak.streak_title;
  
  // Calculate progress to next milestone
  let progressPercent = 0;
  let daysToNext = 0;
  if (nextMilestone) {
    const prevMilestone = currentTitle ? currentTitle.days : 0;
    const range = nextMilestone.days - prevMilestone;
    const progress = currentStreak - prevMilestone;
    progressPercent = Math.min((progress / range) * 100, 100);
    daysToNext = nextMilestone.days - currentStreak;
  } else {
    progressPercent = 100; // Max level reached
  }

  return (
    <>
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className}`}>
        {/* Header with current title */}
        <div className={`p-4 bg-gradient-to-r ${currentTitle?.color || 'from-emerald-500 to-teal-500'} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{currentTitle?.icon || '💰'}</span>
              <div>
                <p className="text-xs uppercase tracking-wider opacity-90">Savings Streak</p>
                <h3 className="font-bold text-lg">
                  {currentTitle?.title || 'Getting Started'}
                </h3>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black">{currentStreak}</p>
              <p className="text-xs opacity-90">days</p>
            </div>
          </div>
        </div>

        {/* Progress section */}
        <div className="p-4">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">${streak.total_amount_saved?.toFixed(0) || 0}</p>
              <p className="text-xs text-gray-500">Total Saved</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{streak.total_days_saved || 0}</p>
              <p className="text-xs text-gray-500">Days Saved</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{streak.longest_streak || 0}</p>
              <p className="text-xs text-gray-500">Best Streak</p>
            </div>
          </div>

          {/* Next milestone progress */}
          {nextMilestone && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Next: {nextMilestone.title}
                </span>
                <span className="text-sm text-gray-500">
                  {daysToNext} days to go
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${nextMilestone.color} transition-all duration-500`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-lg">{nextMilestone.icon}</span>
                <span className="text-xs text-gray-500">{nextMilestone.days} day streak</span>
              </div>
            </div>
          )}

          {/* Milestones achieved */}
          {streak.milestones_reached?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {streak.milestones_reached.map((m, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs"
                  title={m.title}
                >
                  {m.icon}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Celebration Modal */}
      {showCelebration && celebratedMilestone && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden animate-bounce-in">
            <div className={`p-8 bg-gradient-to-br ${celebratedMilestone.color} text-white text-center`}>
              <span className="text-6xl block mb-4">{celebratedMilestone.icon}</span>
              <h2 className="text-2xl font-black mb-2">NEW MILESTONE!</h2>
              <p className="text-lg font-bold">{celebratedMilestone.title}</p>
              <p className="text-sm opacity-90 mt-1">{celebratedMilestone.days} Day Savings Streak</p>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-600 mb-4">
                You've been adding to your savings for <strong>{celebratedMilestone.days} days</strong> straight! 
                Keep it up! 🎉
              </p>
              <button
                onClick={dismissCelebration}
                className={`w-full py-3 bg-gradient-to-r ${celebratedMilestone.color} text-white rounded-xl font-bold hover:shadow-lg transition-all`}
                data-testid="dismiss-streak-celebration"
              >
                Keep Stacking! 💪
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes bounce-in {
          0% { transform: scale(0.5); opacity: 0; }
          70% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
      `}</style>
    </>
  );
}
